<script>
import CreateModal from '../CreateModal.vue';
import OperatingHours from '../OperatingHours.vue';
import OperatingHoursOverrides from '../OperatingHoursOverrides/OperatingHoursOverrides.vue';
import AoikumoOutletField from '../AoikumoOutletField.vue';
import AoikumoDisabledTooltip from 'dashboard/components/dahreply/AoikumoDisabledTooltip.vue';
import { useAlert } from 'dashboard/composables';

export default {
  name: 'GeneralTab',
  props: {
    selectedBranch: {
      type: Object,
      default: null,
    }
  },
  components: {
    CreateModal,
    OperatingHours,
    OperatingHoursOverrides,
    AoikumoOutletField,
    AoikumoDisabledTooltip,
  },
  data() {
    return {
      showEditModal: false,
      providerLinks: null,
    };
  },
  computed: {
    branchId() {
      return this.selectedBranch?.branch_id || this.selectedBranch?.id;
    },
    isAoikumoConnected() {
      return this.$store.getters['aoikumo/isConnected'];
    },
    isAoikumoLinked() {
      return this.$store.getters['aoikumo/isLinkedBranch']({
        provider_links: this.providerLinks,
      });
    },
  },
  watch: {
    // The connection status can still be loading on a cold page load, so
    // reload the links once it arrives as well as when the branch changes.
    branchId: {
      handler: 'loadProviderLinks',
      immediate: true,
    },
    isAoikumoConnected: 'loadProviderLinks',
  },
  methods: {
    // provider_links only comes back from the v2 branch API.
    async loadProviderLinks() {
      if (!this.branchId || !this.isAoikumoConnected) {
        this.providerLinks = null;
        return;
      }
      try {
        const data = await this.$store.dispatch(
          'branchV2/fetchBranch',
          this.branchId
        );
        this.providerLinks = data?.branch?.provider_links || null;
      } catch (error) {
        this.providerLinks = null;
      }
    },
    handleEditBranch() {
      this.showEditModal = true;
    },
    handleCloseEdit() {
      this.showEditModal = false;
    },
    async refreshPage() {
      if (!this.selectedBranch) return;
      await this.$store.dispatch('branch/fetchBranch', this.selectedBranch.branch_id || this.selectedBranch.id);
    },
  },
};
</script>

<template>
  <div class="px-8 space-y-6">
    <div class="shadow-md border rounded-lg p-6 space-y-6">
      <!-- Modals -->
      <CreateModal 
        v-if="showEditModal" 
        :is-edit="true" 
        :branch="selectedBranch" 
        @close="handleCloseEdit" 
        @refresh="refreshPage" 
      />

      <!-- Header -->
      <div class="flex justify-between items-center border-b pb-4">
        <h1 class="text-xl font-semibold text-slate-900 dark:text-white">
          General
        </h1>
        <AoikumoDisabledTooltip :disabled="isAoikumoLinked">
          <woot-button
            variant="smooth"
            color-scheme="primary"
            icon="edit"
            :is-disabled="isAoikumoLinked"
            @click="handleEditBranch"
          >
            Edit
          </woot-button>
        </AoikumoDisabledTooltip>
      </div>

      <!-- Branch Details -->
      <div class="space-y-4">
        <div>
          <p class="text-slate-500 text-sm">Branch Name</p>
          <p class="text-lg font-medium text-slate-900 dark:text-white">
            {{ selectedBranch.branch_name || selectedBranch.name || '-' }}
          </p>
        </div>
        <div>
          <p class="text-slate-500 text-sm">Phone Number</p>
          <p class="text-lg font-medium text-slate-900 dark:text-white">
            {{ selectedBranch.phone_number || '-' }}
          </p>
        </div>
        <div>
          <p class="text-slate-500 text-sm">Address</p>
          <p class="text-lg font-medium text-slate-900 dark:text-white">
            {{ selectedBranch.address || '-' }}
          </p>
        </div>
        <AoikumoOutletField
          v-if="branchId"
          :branch-id="branchId"
          :branch-name="selectedBranch.branch_name || selectedBranch.name || ''"
          :provider-links="providerLinks"
          @updated="loadProviderLinks"
        />
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <OperatingHours :selectedBranch="selectedBranch" />
        <OperatingHoursOverrides :selectedBranch="selectedBranch" />
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.animate-fadeIn {
  animation: fadeIn 0.2s ease-in-out;
}
</style>